import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Loader2, MessageSquareText, ShieldCheck, Store, Truck, MapPin, Clock } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useCart } from "@/context/CartContext";
import { api } from "@/lib/api";
import { money } from "@/data/products";

const input = "w-full h-12 rounded-xl border-2 border-[#181203]/30 bg-white/60 px-4 outline-none focus:border-[#181203]";
const SLOTS = ["Sunday - Wednesday 8pm-11pm", "Thursday - Saturday 10am-12am"];
const PICKUP_ADDRESS = "2725 Rainbow Forest Dr, Decatur, GA 30034";

export const CheckoutDialog = ({ open, onOpenChange }) => {
  const { items, lines, clear, setOpen } = useCart();
  const [step, setStep] = useState("form");
  const [method, setMethod] = useState("delivery");
  const [slot, setSlot] = useState(SLOTS[0]);
  const [form, setForm] = useState({ name: "", email: "", phone: "", address: "" });
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");

  const close = (v) => {
    onOpenChange(v);
    if (!v) {
      setStep("form");
      setMethod("delivery");
      setCode("");
      setOrder(null);
      setError("");
    }
  };

  const submitSetup = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    const payload = method === "pickup"
      ? { name: form.name, method: "pickup", phone: form.phone, slot }
      : { ...form, method: "delivery" };
    try {
      await api.post("/delivery-requests", payload);
      setStep("code");
      toast.success(method === "pickup"
        ? "Pickup set — watch your phone for your confirmation code."
        : "Delivery request received — watch your phone for the quote and code.");
    } catch {
      setError("Could not submit. Please check your details and try again.");
    } finally {
      setBusy(false);
    }
  };

  const submitOrder = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const payload = method === "pickup"
        ? { name: form.name, email: form.email, method: "pickup", phone: form.phone, slot, code, items }
        : { ...form, method: "delivery", code, items };
      const { data } = await api.post("/orders", payload);
      setOrder(data);
      const { data: pay } = await api.post("/payments/checkout", {
        items: lines.map((l) => ({ lookup_key: l.product_id === "trio" ? "sj_trio" : `sj_${l.product_id}`, quantity: l.qty })),
        origin_url: window.location.origin,
        order_id: data.id,
      });
      clear();
      window.location.href = pay.checkout_url;
    } catch (err) {
      if (err?.response?.status === 403) {
        setError("Invalid or expired code. Please wait for your text.");
      } else if (err?.response?.status === 403 || err?.response?.status === 400) {
        setError("Payment could not start. Please try again.");
      } else if (!err?.response) {
        setError("Network error — please try again.");
      } else {
        setError("Checkout failed. Please try again.");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent data-testid="checkout-dialog" className="bg-[#FFF4D6] text-[#181203] rounded-3xl max-h-[90vh] overflow-y-auto">
        {order ? (
          <div data-testid="checkout-success" className="text-center py-6">
            <CheckCircle2 className="w-14 h-14 mx-auto text-[#2E7D32]" />
            <DialogTitle className="font-display text-3xl font-extrabold mt-4">Opening secure payment…</DialogTitle>
            <DialogDescription className="mt-2 text-[#594519]">
              Order #{String(order?.id ?? "").slice(-6)} · {money(order?.subtotal)} · {order?.method === "pickup" ? "Pickup" : "Delivery quoted separately"}. You'll be redirected to card payment.
            </DialogDescription>
          </div>
        ) : step === "form" ? (
          <form data-testid="delivery-form" onSubmit={submitSetup} className="space-y-4">
            <DialogHeader>
              <DialogTitle className="font-display text-3xl font-extrabold">How would you like it?</DialogTitle>
              <DialogDescription className="text-[#594519]">Delivery is quoted by text. Pickup is free in Decatur.</DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              {[
                { id: "delivery", label: "Delivery", icon: Truck, note: "Charged separately · all of Georgia" },
                { id: "pickup", label: "Pickup", icon: Store, note: "Free · Decatur, GA 30034" },
              ].map((m) => (
                <button
                  type="button"
                  key={m.id}
                  data-testid={`method-${m.id}`}
                  onClick={() => setMethod(m.id)}
                  className={`rounded-2xl border-2 p-4 text-left transition-colors ${method === m.id ? "border-[#181203] bg-[#181203]/5" : "border-[#181203]/20 hover:border-[#181203]/50"}`}
                >
                  <m.icon className="w-5 h-5 mb-2" />
                  <span className="font-display font-extrabold block">{m.label}</span>
                  <span className="text-xs text-[#594519]">{m.note}</span>
                </button>
              ))}
            </div>
            {method === "pickup" ? (
              <>
                <div className="space-y-2">
                  <p className="text-sm font-bold flex items-center gap-2"><Clock className="w-4 h-4" /> Choose your pickup slot</p>
                  {SLOTS.map((s) => (
                    <label key={s} data-testid={`slot-${s.slice(0, 20).replace(/\W+/g, "-").toLowerCase()}`} className={`flex items-center gap-3 rounded-xl border-2 px-4 py-3 cursor-pointer text-sm font-semibold transition-colors ${slot === s ? "border-[#181203] bg-[#181203]/5" : "border-[#181203]/20 hover:border-[#181203]/50"}`}>
                      <input type="radio" name="slot" checked={slot === s} onChange={() => setSlot(s)} className="accent-[#181203]" />
                      {s}
                    </label>
                  ))}
                </div>
                <input data-testid="delivery-name-input" required placeholder="First and last name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={input} />
                <input data-testid="delivery-phone-input" required type="tel" placeholder="Phone number to be texted on" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={input} />
                <input data-testid="checkout-email-input" required type="email" placeholder="Email (for your order confirmation)" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={input} />
                <p data-testid="pickup-address" className="text-sm font-semibold flex items-start gap-2 rounded-xl bg-[#181203]/5 p-3">
                  <MapPin className="w-4 h-4 mt-0.5 shrink-0" />
                  Pickup address: {PICKUP_ADDRESS} — we'll text this to you.
                </p>
              </>
            ) : (
              <>
                <input data-testid="delivery-name-input" required placeholder="First and last name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={input} />
                <input data-testid="checkout-email-input" required type="email" placeholder="Email (for your order confirmation)" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={input} />
                <input data-testid="delivery-phone-input" required type="tel" placeholder="Phone number (for your texted quote)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={input} />
                <input data-testid="delivery-address-input" required placeholder="Delivery address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className={input} />
              </>
            )}
            {error && <p data-testid="delivery-error" className="text-sm font-bold text-[#B5122F]">{error}</p>}
            <button data-testid="delivery-submit-button" disabled={busy || !items.length} className="w-full h-14 rounded-full bg-[#181203] text-[#FFD000] font-bold inline-flex items-center justify-center gap-2 disabled:opacity-50">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <MessageSquareText className="w-5 h-5" />} Continue
            </button>
          </form>
        ) : (
          <form data-testid="code-step" onSubmit={submitOrder} className="space-y-4">
            <DialogHeader>
              <DialogTitle className="font-display text-3xl font-extrabold">Enter your code</DialogTitle>
              <DialogDescription data-testid="code-notice" className="text-[#594519]">
                {method === "pickup"
                  ? "You'll be texted your pickup confirmation with a 5-digit code — please be on the lookout in your text messages for the delivery code to finalize the order."
                  : "You'll be texted a delivery quote and a 5-digit code — please be on the lookout in your text messages for the delivery code to finalize the order."}{" "}
                Enter it below to finish checkout.
              </DialogDescription>
            </DialogHeader>
            <input
              data-testid="checkout-code-input"
              required
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={5}
              placeholder="• • • • •"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 5))}
              className="w-full h-16 rounded-2xl border-2 border-[#181203]/40 bg-white/70 text-center font-display text-3xl font-extrabold tracking-[0.6em] outline-none focus:border-[#181203]"
            />
            {error && <p data-testid="code-error" className="text-sm font-bold text-[#B5122F]">{error}</p>}
            <button data-testid="checkout-submit-button" disabled={busy || code.length !== 5} className="w-full h-14 rounded-full bg-[#181203] text-[#FFD000] font-bold inline-flex items-center justify-center gap-2 disabled:opacity-50">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-5 h-5" />} Pay {money(lines.reduce((s, l) => s + l.total, 0))}
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
